import React, { type JSX } from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import { confirmAllBets } from '@mobi/betslip/Store/Workflow/asyncActions'
import { state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { Money } from '@core/Components/Text/Money'
import { startListening } from '@core/Store/listenerMiddleware'
import { ConfirmBetslipBetsSucceeded, QuickbetClosed } from '../Quickbet/signals'
import {
  FavouriteNumbersColumn,
  FavouriteNumbersDataTable,
  FavouriteNumbersRow,
  FavouriteNumbersSection,
  Filler,
} from './FavouriteNumbers.styles'
import { FavouriteNumbersBettingDrawer } from './FavouriteNumbersBettingDrawer'
import { FavouriteNumbersBettingState, clearSelections } from './Store'
import { selectFavouriteNumbersBettingState } from './Store/selectors'
import { calculateEasyBetCost, calculatePotLuckCost, costCalculator } from './calculator'

const EmptyBettingDrawerStyled = styled('div')({
  height: '8rem',
})
interface FavouriteNumbersBetSummaryProps {
  mode: 'easyBet' | 'potLuck'
}

function isGameSelectionValid(numberOfSelections: number): boolean {
  return numberOfSelections >= 7 && numberOfSelections <= 16
}

export function isEasyBetValid(
  gameOneSelections: number,
  gameTwoSelections: number,
  poolSelections: number
): boolean {
  return (
    poolSelections > 0 &&
    isGameSelectionValid(gameOneSelections) &&
    (isGameSelectionValid(gameTwoSelections) || gameTwoSelections == 0)
  )
}

function isPotLuckBetValid(potLuckNumbers: number, potLuckBets: number, numberOfPools: number) {
  return potLuckNumbers > 0 && potLuckBets > 0 && numberOfPools > 0
}

export const FavouriteNumbersBetSummary = ({
  mode,
}: FavouriteNumbersBetSummaryProps): JSX.Element | null => {
  const { gameOne, gameTwo, pools, potLuckBets } = useSelector(selectFavouriteNumbersBettingState)

  return (
    <FavouriteNumbersBetSummaryComponent
      mode={mode}
      pools={pools}
      gameOne={gameOne}
      gameTwo={gameTwo}
      potLuckBets={potLuckBets}
    />
  )
}

const FavouriteNumbersBetSummaryComponent = ({
  gameOne,
  gameTwo,
  pools,
  potLuckBets,
  mode,
}: Pick<FavouriteNumbersBettingState, 'gameOne' | 'gameTwo' | 'pools' | 'potLuckBets'> &
  FavouriteNumbersBetSummaryProps): JSX.Element => {
  const gameOneNumbers = Math.min(gameOne.length, 16)
  const gameTwoNumbers = Math.min(gameTwo.length, 16)
  const numberOfPools = pools.length
  const potLuckNumbers = gameOne[0] ?? 0
  const betCost =
    mode === 'easyBet'
      ? calculateEasyBetCost(gameOneNumbers, gameTwoNumbers, numberOfPools)
      : calculatePotLuckCost(potLuckNumbers, potLuckBets, numberOfPools)
  const isValidBet =
    mode === 'easyBet'
      ? isEasyBetValid(gameOne.length, gameTwo.length, numberOfPools)
      : isPotLuckBetValid(potLuckNumbers, potLuckBets, numberOfPools)

  const dispatch = useDispatch()

  React.useEffect(() => {
    const clearAllSelections = () => {
      dispatch(clearSelections())
    }

    const quickbetSubscription = QuickbetClosed.signal$
      .withLatestFrom(quickbetState$, (_: unknown, quickbetState) => ({ quickbetState }))
      .subscribe(({ quickbetState }) => {
        if (!quickbetState.get('betPlaced')) return
        clearAllSelections()
      })

    const betslipSubscription = ConfirmBetslipBetsSucceeded.signal$.subscribe(clearAllSelections)

    const newBetSlipDisposeSubscription = startListening({
      actionCreator: confirmAllBets.fulfilled,
      effect: clearAllSelections,
    })

    return () => {
      clearAllSelections()
      newBetSlipDisposeSubscription()
      quickbetSubscription.dispose()
      betslipSubscription.dispose()
    }
  }, [dispatch])

  return (
    <FavouriteNumbersSection data-testid='favnum-betsummary'>
      <FavouriteNumbersDataTable>
        {mode == 'easyBet' && (
          <FavouriteNumbersRow>
            <FavouriteNumbersColumn data-tid-game1-numbers=''>
              Game 1 - {gameOneNumbers} numbers
            </FavouriteNumbersColumn>
            <FavouriteNumbersColumn>
              <span data-tid-game1-cost=''>
                <Filler>&nbsp;</Filler>
                <Money amount={costCalculator(gameOneNumbers)} />
              </span>
            </FavouriteNumbersColumn>
          </FavouriteNumbersRow>
        )}
        {mode == 'easyBet' && (
          <FavouriteNumbersRow>
            <FavouriteNumbersColumn data-tid-game-2-numbers=''>
              Game 2 - {gameTwoNumbers} numbers
            </FavouriteNumbersColumn>
            <FavouriteNumbersColumn>
              <span data-tid-game2-cost=''>
                <Filler>+</Filler>
                <Money amount={costCalculator(gameTwoNumbers)} />
              </span>
            </FavouriteNumbersColumn>
          </FavouriteNumbersRow>
        )}
        {mode == 'potLuck' && (
          <FavouriteNumbersRow>
            <FavouriteNumbersColumn data-tid-potluck-numbers=''>
              {potLuckNumbers} numbers
            </FavouriteNumbersColumn>
            <FavouriteNumbersColumn>
              <span data-tid-potluck-cost=''>
                <Filler>&nbsp;</Filler>
                <Money amount={costCalculator(potLuckNumbers)} />
              </span>
            </FavouriteNumbersColumn>
          </FavouriteNumbersRow>
        )}
        <FavouriteNumbersRow>
          <FavouriteNumbersColumn data-tid-number-of-pool-dates-label=''>
            Number of pool dates
          </FavouriteNumbersColumn>
          <FavouriteNumbersColumn>
            <span data-tid-number-of-pool-dates=''>
              <Filler>x</Filler> {numberOfPools}
            </span>
          </FavouriteNumbersColumn>
        </FavouriteNumbersRow>
        {mode == 'potLuck' && (
          <FavouriteNumbersRow>
            <FavouriteNumbersColumn data-tid-number-of-bets-label=''>
              Number of bets
            </FavouriteNumbersColumn>
            <FavouriteNumbersColumn>
              <span data-tid-pot-luck-bets=''>
                <Filler>x</Filler> {potLuckBets}
              </span>
            </FavouriteNumbersColumn>
          </FavouriteNumbersRow>
        )}
        <FavouriteNumbersRow>
          <FavouriteNumbersColumn data-tid-bet-cost=''>Bet cost</FavouriteNumbersColumn>
          <FavouriteNumbersColumn>
            <span data-tid-bet-cost=''>
              <Filler>&nbsp;</Filler>
              <Money amount={betCost} />
            </span>
          </FavouriteNumbersColumn>
        </FavouriteNumbersRow>
      </FavouriteNumbersDataTable>
      <FavouriteNumbersBettingDrawer open={isValidBet} />
      {isValidBet && <EmptyBettingDrawerStyled />}
    </FavouriteNumbersSection>
  )
}
